import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  storeGetOrDefault, hydrateFromServer,
  DEF_PRODUCTS, DEF_HERO, DEF_DEALERS,
  DEF_RESOURCES, DEF_ADVANTAGES, DEF_NAV,
  DEF_SETTINGS, DEF_STATS, DEF_FOOTER, DEF_ABOUT, DEF_GEM,
  DEF_HOW_IT_WORKS, DEF_COMPANY_TRUST, DEF_CONTACT_CTA, DEF_HOME_SHOWCASE,
  DEF_HOME_VIDEOS,
  DEF_OTHER_PRODUCTS,
} from './store';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [products,   setProducts]   = useState(() => storeGetOrDefault('products',   DEF_PRODUCTS));
  const [heroSlides, setHeroSlides] = useState(() => storeGetOrDefault('hero',       DEF_HERO));
  const [dealers,    setDealers]    = useState(() => storeGetOrDefault('dealers',    DEF_DEALERS));
  const [resources,  setResources]  = useState(() => storeGetOrDefault('resources',  DEF_RESOURCES));
  const [advantages, setAdvantages] = useState(() => storeGetOrDefault('advantages', DEF_ADVANTAGES));
  const [navItems,   setNavItems]   = useState(() => storeGetOrDefault('navItems',   DEF_NAV));
  const [settings,   setSettings]   = useState(() => storeGetOrDefault('settings',   DEF_SETTINGS));
  const [stats,      setStats]      = useState(() => storeGetOrDefault('stats',      DEF_STATS));
  const [footerData, setFooterData] = useState(() => storeGetOrDefault('footer',     DEF_FOOTER));
  const [about,      setAbout]      = useState(() => storeGetOrDefault('about',      DEF_ABOUT));
  const [gem,          setGem]          = useState(() => storeGetOrDefault('gem',          DEF_GEM));
  const [howItWorks,   setHowItWorks]   = useState(() => storeGetOrDefault('howItWorks',   DEF_HOW_IT_WORKS));
  const [companyTrust, setCompanyTrust] = useState(() => storeGetOrDefault('companyTrust', DEF_COMPANY_TRUST));
  const [contactCTA,   setContactCTA]   = useState(() => storeGetOrDefault('contactCTA',   DEF_CONTACT_CTA));
  const [homeShowcase,  setHomeShowcase]  = useState(() => storeGetOrDefault('homeShowcase',  DEF_HOME_SHOWCASE));
  const [homeVideos,    setHomeVideos]    = useState(() => storeGetOrDefault('homeVideos',    DEF_HOME_VIDEOS));
  const [otherProducts, setOtherProducts] = useState(() => storeGetOrDefault('otherProducts', DEF_OTHER_PRODUCTS));

  const refresh = useCallback((e) => {
    const key = e?.detail?.key;
    if (!key || key === 'products')   setProducts(storeGetOrDefault('products',   DEF_PRODUCTS));
    if (!key || key === 'hero')       setHeroSlides(storeGetOrDefault('hero',     DEF_HERO));
    if (!key || key === 'dealers')    setDealers(storeGetOrDefault('dealers',     DEF_DEALERS));
    if (!key || key === 'resources')  setResources(storeGetOrDefault('resources', DEF_RESOURCES));
    if (!key || key === 'advantages') setAdvantages(storeGetOrDefault('advantages', DEF_ADVANTAGES));
    if (!key || key === 'navItems')   setNavItems(storeGetOrDefault('navItems',   DEF_NAV));
    if (!key || key === 'settings')   setSettings(storeGetOrDefault('settings',   DEF_SETTINGS));
    if (!key || key === 'stats')      setStats(storeGetOrDefault('stats',         DEF_STATS));
    if (!key || key === 'footer')     setFooterData(storeGetOrDefault('footer',   DEF_FOOTER));
    if (!key || key === 'about')      setAbout(storeGetOrDefault('about',         DEF_ABOUT));
    if (!key || key === 'gem')          setGem(storeGetOrDefault('gem',             DEF_GEM));
    if (!key || key === 'howItWorks')   setHowItWorks(storeGetOrDefault('howItWorks',   DEF_HOW_IT_WORKS));
    if (!key || key === 'companyTrust') setCompanyTrust(storeGetOrDefault('companyTrust', DEF_COMPANY_TRUST));
    if (!key || key === 'contactCTA')   setContactCTA(storeGetOrDefault('contactCTA',   DEF_CONTACT_CTA));
    if (!key || key === 'homeShowcase')  setHomeShowcase(storeGetOrDefault('homeShowcase',  DEF_HOME_SHOWCASE));
    if (!key || key === 'homeVideos')    setHomeVideos(storeGetOrDefault('homeVideos',    DEF_HOME_VIDEOS));
    if (!key || key === 'otherProducts') setOtherProducts(storeGetOrDefault('otherProducts', DEF_OTHER_PRODUCTS));
  }, []);

  useEffect(() => {
    window.addEventListener('zinga-store-update', refresh);
    return () => window.removeEventListener('zinga-store-update', refresh);
  }, [refresh]);

  // Cross-tab sync: when localStorage changes in another tab, refresh that key
  useEffect(() => {
    const handleStorage = (e) => {
      if (!e.key) return;
      const LSKEY_TO_KEY = {
        zinga_products:   'products',
        zinga_hero:       'hero',
        zinga_dealers:    'dealers',
        zinga_resources:  'resources',
        zinga_advantages: 'advantages',
        zinga_nav:        'navItems',
        zinga_settings:   'settings',
        zinga_stats:      'stats',
        zinga_footer:     'footer',
        zinga_about:      'about',
        zinga_gem:           'gem',
        zinga_how_it_works:  'howItWorks',
        zinga_company_trust: 'companyTrust',
        zinga_contact_cta:   'contactCTA',
        zinga_home_showcase:  'homeShowcase',
        zinga_home_videos:    'homeVideos',
        zinga_other_products: 'otherProducts',
      };
      const key = LSKEY_TO_KEY[e.key];
      if (key) refresh({ detail: { key } });
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [refresh]);

  // On startup, pull latest data from the server (if API is configured)
  // This ensures changes made by admin on any device are immediately visible
  useEffect(() => {
    hydrateFromServer().then(changed => {
      if (changed) refresh({});
      // After hydration, apply language preference from store (works on fresh devices)
      try {
        const raw = localStorage.getItem('zinga_language');
        const langObj = raw ? JSON.parse(raw) : null;
        const code = langObj?.code || (typeof langObj === 'string' ? langObj : null);
        if (code && code !== 'en') {
          const val = '/en/' + code;
          const applyGT = (attempts) => {
            const sel = document.querySelector('.goog-te-combo');
            if (sel) { sel.value = code; sel.dispatchEvent(new Event('change')); }
            else if (attempts > 0) setTimeout(() => applyGT(attempts - 1), 700);
          };
          document.cookie = `googtrans=${val}; path=/;`;
          document.cookie = `googtrans=${val}; path=/; domain=${window.location.hostname};`;
          document.cookie = `googtrans=${val}; path=/; domain=.${window.location.hostname};`;
          setTimeout(() => applyGT(6), 1500);
        }
      } catch(e) {}
    });
  }, [refresh]);

  // When the language key changes (startup hydration OR 30s polling), apply GT immediately
  useEffect(() => {
    const onLangChange = (e) => {
      if (e?.detail?.key !== 'language') return;
      try {
        const raw = localStorage.getItem('zinga_language');
        const langObj = raw ? JSON.parse(raw) : null;
        const code = langObj?.code || (typeof langObj === 'string' ? langObj : null);
        if (!code || code === 'en') {
          const sel = document.querySelector('.goog-te-combo');
          if (sel) { sel.value = ''; sel.dispatchEvent(new Event('change')); }
          return;
        }
        const val = '/en/' + code;
        document.cookie = `googtrans=${val}; path=/;`;
        document.cookie = `googtrans=${val}; path=/; domain=${window.location.hostname};`;
        document.cookie = `googtrans=${val}; path=/; domain=.${window.location.hostname};`;
        const applyGT = (attempts) => {
          const sel = document.querySelector('.goog-te-combo');
          if (sel) { sel.value = code; sel.dispatchEvent(new Event('change')); }
          else if (attempts > 0) setTimeout(() => applyGT(attempts - 1), 600);
        };
        applyGT(10); // up to 10 retries × 600 ms = 6 s window
      } catch (_) {}
    };
    window.addEventListener('zinga-store-update', onLangChange);
    return () => window.removeEventListener('zinga-store-update', onLangChange);
  }, []);

  // Poll the server every 30 seconds so all devices stay in sync
  useEffect(() => {
    const id = setInterval(async () => {
      const changed = await hydrateFromServer();
      if (changed) refresh({});
    }, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  return (
    <AppContext.Provider value={{
      products,   heroSlides,   dealers,      resources,
      advantages, navItems,     settings,     stats,
      footerData, about,        gem,
      howItWorks, companyTrust, contactCTA,   homeShowcase,
      homeVideos,
      otherProducts,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppData = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppData must be used inside AppProvider');
  return ctx;
};
